import { FC, useMemo } from 'react';
import { useAtom } from 'jotai';
import { InlineNotification, Tag } from '@carbon/react';
import searchAtomPair, { type SearchParams } from 'atoms/search-atom';
import { ConfidenceBreakdown } from 'components/confidence-breakdown';
import { formatConfidence } from 'utils/number';
import {
  AuditLogTable,
  AgentRunDetail,
  EventExpanded,
} from 'pages/documents/structuring/audit-log';
import style from 'pages/documents/structuring/audit-log/audit-log.module.scss';
import type {
  DoActivityFeedAgentRunRow,
  DoActivityFeedItem,
  DoActivityFeedRequest,
  DoActivityFeedResponse,
  DoActivityFeedSection,
  DoActivityFeedSuggestion,
} from './types';

// DO structuring activity log. The DO activity feed
// (POST /queries/division_order_structuring_activity_feeds) serializes the same
// agent_run + event envelope as the Entity Match Agent feed and the AFE feed, so
// this component reuses the shared audit-log renderers
// (pages/documents/structuring/audit-log) and owns only its own data fetch via
// `searchAtomPair`. Mirror of pages/afe-catalog/structuring/agent-audit-log.tsx,
// with the AFE section vocab (identification/well/cost) swapped for the DO vocab
// (overview/interest/well) and the scope keys swapped for the DO-specific
// division_order_target_id / division_order_line_item_id.
//
// Well-section agent runs DO persist suggestion rows (confidence_factors +
// review_reason), so the agent-run detail renders them via the same thin
// SuggestionsList / ConfidenceBreakdown convention the AFE well log and the
// Entity Match (Parties) log use. Overview/interest runs carry no suggestions
// and render the empty state.

type FeedRequestParams = SearchParams & DoActivityFeedRequest;

// Well-match suggestion row — mirror of the AFE well log's row (same byte-
// identical Well suggestion shape) reusing the shared, exported
// ConfidenceBreakdown. Pure (no atoms / no data coupling). The AFE equivalent is
// a private const in a read-only-for-this-change file, so per the established
// per-page convention (see party-audit-log.tsx) it lives here rather than being
// imported.
const WellMatchSuggestionRow: FC<{ suggestion: DoActivityFeedSuggestion }> = ({
  suggestion: s,
}) => (
  <div className={style.suggestionItem}>
    <div className={style.suggestionHeader}>
      <span className={style.suggestionName}>
        {s.suggestedWellName || `Well #${s.suggestedWellId ?? '—'}`}
      </span>
      <div className={style.suggestionMeta}>
        <span className={style.suggestionScore}>
          {formatConfidence(s.confidenceScore)}
        </span>
        <Tag type="gray" size="sm">
          {s.status}
        </Tag>
      </div>
    </div>
    {s.reviewReason && (
      <p className={style.suggestionReason}>Review reason: {s.reviewReason}</p>
    )}
    {s.confidenceFactors && s.confidenceFactors.length > 0 && (
      <ConfidenceBreakdown factors={s.confidenceFactors} />
    )}
  </div>
);

const SuggestionsList: FC<{ rows: DoActivityFeedSuggestion[] }> = ({
  rows,
}) => {
  if (rows.length === 0)
    return (
      <p className={style.muted}>No suggestions were saved for this run.</p>
    );
  return (
    <div className={style.suggestionsList}>
      {rows.map((s) => (
        <WellMatchSuggestionRow key={s.id} suggestion={s} />
      ))}
    </div>
  );
};

interface Props {
  documentId: number | string;
  /**
   * DO structuring section to scope the feed to. Omit it for the consolidated
   * document-level log: the backend treats a blank section as "all sections",
   * merging overview + interest + well user events and the Well Matching Agent
   * runs into one chronological feed. The well drill-in passes 'well' to scope
   * to a single target.
   */
  section?: DoActivityFeedSection;
  /**
   * Narrow the feed to one interest line (its DOLI SF Id — a STRING, not a
   * numeric PK). When set, the backend scopes the per-line events to this line.
   * Omitted (undefined) shows the whole document's feed.
   */
  divisionOrderLineItemId?: string;
  /**
   * Narrow the feed to one DO target (its DivisionOrderTarget id). The Well
   * Match Review drill-in passes this to scope the per-well events + Well
   * Matching Agent runs to a single target. Omitted for the document-level log.
   */
  divisionOrderTargetId?: number | string;
}

const renderAgentRun = (row: DoActivityFeedAgentRunRow) => {
  const d = row.data;
  return (
    <AgentRunDetail
      meta={d}
      runId={row.id}
      suggestionsLabel="Suggestions Persisted"
      suggestionsCount={d.suggestions?.length ?? 0}
      suggestions={<SuggestionsList rows={d.suggestions ?? []} />}
    />
  );
};

const renderExpanded = (row: DoActivityFeedItem) => {
  return row.activityType === 'agent_run' ? (
    renderAgentRun(row)
  ) : (
    <EventExpanded
      user={row.data.user}
      action={row.data.action}
      description={row.data.description}
    />
  );
};

export const AgentAuditLog: FC<Props> = ({
  documentId,
  section,
  divisionOrderLineItemId,
  divisionOrderTargetId,
}) => {
  const { searchAtom } = useMemo(
    () =>
      searchAtomPair<DoActivityFeedItem, FeedRequestParams>(
        { href: '/queries/division_order_structuring_activity_feeds' },
        {
          query: {
            document_id: documentId,
            // Omit `section` entirely when unset so the BE's `.presence` check
            // sees a blank section and merges every section's events + the agent
            // runs into one feed (the document-level Activity Log).
            ...(section ? { section } : {}),
            // Scope to one line / target when given (omit the key entirely
            // otherwise so the BE's `.present?` narrowing check sees nothing).
            ...(divisionOrderLineItemId !== null &&
            divisionOrderLineItemId !== undefined
              ? { division_order_line_item_id: divisionOrderLineItemId }
              : {}),
            ...(divisionOrderTargetId !== null &&
            divisionOrderTargetId !== undefined
              ? { division_order_target_id: divisionOrderTargetId }
              : {}),
            page: 1,
            page_size: 50,
            sort_direction: 'desc',
          },
        }
      ),
    [documentId, section, divisionOrderLineItemId, divisionOrderTargetId]
  );

  const [searchState] = useAtom(searchAtom);

  const activities =
    (searchState.data as unknown as DoActivityFeedResponse | undefined)
      ?.results ?? [];

  if (searchState.error && activities.length === 0) {
    return (
      <InlineNotification
        kind="error"
        lowContrast
        hideCloseButton
        title="Unable to load activity"
        subtitle={searchState.error}
        style={{ maxWidth: '100%' }}
      />
    );
  }

  if (searchState.loading && activities.length === 0) {
    return <p className={style.empty}>Loading activity…</p>;
  }

  return (
    <AuditLogTable
      rows={activities}
      renderExpanded={renderExpanded}
      emptyMessage="No activity yet."
    />
  );
};
